import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useWorkspace } from '../context/WorkspaceContext';
import ImageEditorModal from '../components/game/ImageEditorModal';
import ImageEditorNewDocumentDialog from '../components/game/ImageEditorNewDocumentDialog';
import ImageEditorOpenDialog from '../components/game/ImageEditorOpenDialog';
import { normalizeImageEditorFormat } from '../lib/imageEditorFormats';
import { saveEditedAssetVariant } from '../lib/saveEditedAssetVariant';
import { saveImageEditorDraft } from '../lib/saveImageEditorDraft';
import {
  IMAGE_EDITOR_PROJECT_KIND,
} from '../lib/imageEditorProjectFormat';
import {
  listRecentImageEditorDocuments,
  saveLayeredEditorProject,
} from '../lib/imageEditorProjectFiles';
import {
  canWriteFilesNode,
  deleteFilesNode,
  formatBulkFilesResultMessage,
  runBulkFilesOperation,
} from '../lib/companyFiles';

/**
 * @param {unknown} asset
 * @returns {string}
 */
function galleryImageUrl(asset) {
  if (!asset || typeof asset !== 'object') return '';
  const download =
    typeof asset.downloadUrl === 'string' ? asset.downloadUrl.trim() : '';
  const preview =
    typeof asset.previewUrl === 'string' ? asset.previewUrl.trim() : '';
  const image = typeof asset.imageUrl === 'string' ? asset.imageUrl.trim() : '';
  return download || preview || image || '';
}

/**
 * @param {unknown} asset
 * @returns {string}
 */
function galleryLabel(asset) {
  if (!asset || typeof asset !== 'object') return 'asset';
  if (typeof asset.name === 'string' && asset.name.trim()) {
    return asset.name.trim();
  }
  if (typeof asset.assetName === 'string' && asset.assetName.trim()) {
    return asset.assetName.trim();
  }
  if (typeof asset.manifestAssetId === 'string' && asset.manifestAssetId.trim()) {
    return asset.manifestAssetId.trim();
  }
  if (typeof asset.id === 'string' && asset.id.trim()) return asset.id.trim();
  return 'asset';
}

/**
 * @param {number} ms
 * @returns {string}
 */
function formatRelativeUpdated(ms) {
  if (!ms) return 'Recently';
  const delta = Date.now() - ms;
  if (delta < 60_000) return 'Just now';
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  if (delta < 7 * 86_400_000) return `${Math.floor(delta / 86_400_000)}d ago`;
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return 'Recently';
  }
}

/**
 * Standalone AI Image Editor hub — editor-centric landing with New / Open.
 * Project-scoped saves go to the project gallery; no-project saves go to Drafts.
 *
 * @param {{ userData?: { companyId?: string } }} props
 */
export default function ImageEditorHub({ userData }) {
  const { companyId } = useWorkspace();
  const resolvedCompanyId = companyId || userData?.companyId || null;
  const role = userData?.role || null;

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState('');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const [editorError, setEditorError] = useState('');
  const [editorSuccess, setEditorSuccess] = useState('');
  /**
   * @type {[{
   *   kind: 'blank'|'local'|'gallery'|'draft',
   *   imageUrl: string,
   *   assetLabel: string,
   *   sourceAsset?: object|null,
   *   objectUrl?: string|null,
   *   blankSize?: {width:number,height:number},
   *   destinationProjectId?: string|null,
   *   preferredFormat?: string,
   *   draftId?: string|null,
   *   layeredNodeId?: string|null,
   *   layeredKind?: string|null,
   * }|null, Function]}
   */
  const [session, setSession] = useState(null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState('');
  const [recentBusy, setRecentBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lastClickedId, setLastClickedId] = useState(null);

  const projectsRef = useMemo(() => {
    if (!resolvedCompanyId) return null;
    return collection(db, 'companies', resolvedCompanyId, 'game_projects');
  }, [resolvedCompanyId]);

  useEffect(() => {
    if (!projectsRef) {
      setProjects([]);
      return undefined;
    }
    setProjectsLoading(true);
    setProjectsError('');
    const q = query(projectsRef, orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setProjects(rows);
        setProjectsLoading(false);
      },
      (err) => {
        console.error('ImageEditorHub projects:', err);
        setProjectsError(err.message || 'Could not load game projects.');
        setProjectsLoading(false);
      },
    );
    return () => unsub();
  }, [projectsRef]);

  useEffect(
    () => () => {
      if (session?.objectUrl) {
        try {
          URL.revokeObjectURL(session.objectUrl);
        } catch {
          /* ignore */
        }
      }
    },
    [session],
  );

  // Keep Slice 4 recovery capability: probe unfinished/recent work, present as
  // a persistent landing grid instead of a blocking popup.
  useEffect(() => {
    if (!resolvedCompanyId || editorOpen) return undefined;
    const uid = auth.currentUser?.uid || null;
    if (!uid) return undefined;
    let cancelled = false;
    setRecentLoading(true);
    setRecentError('');
    (async () => {
      try {
        const rows = await listRecentImageEditorDocuments({
          companyId: resolvedCompanyId,
          uid,
          maxAgeDays: 7,
          limit: 16,
        });
        if (cancelled) return;
        setRecentDocuments(rows);
        setSelectedIds(new Set());
        setLastClickedId(null);
      } catch (err) {
        console.warn('ImageEditorHub recent documents failed:', err);
        if (!cancelled) {
          setRecentDocuments([]);
          setRecentError(err?.message || 'Could not load recent documents.');
        }
      } finally {
        if (!cancelled) setRecentLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedCompanyId, editorOpen]);

  const closeEditor = () => {
    if (editorBusy) return;
    setEditorOpen(false);
    setSession((prev) => {
      if (prev?.objectUrl) {
        try {
          URL.revokeObjectURL(prev.objectUrl);
        } catch {
          /* ignore */
        }
      }
      return null;
    });
  };

  const openLocalFile = (file) => {
    if (!file || !String(file.type || '').startsWith('image/')) {
      setEditorError('Choose an image file (PNG, JPEG, WebP, etc.).');
      return;
    }
    setEditorError('');
    setEditorSuccess('');
    const objectUrl = URL.createObjectURL(file);
    const base =
      String(file.name || 'local')
        .replace(/\.[^.]+$/, '')
        .replace(/[^a-zA-Z0-9_-]+/g, '_')
        .slice(0, 60) || 'local';
    setSession({
      kind: 'local',
      imageUrl: objectUrl,
      assetLabel: base,
      objectUrl,
      destinationProjectId: null,
      preferredFormat: 'png',
      draftId: null,
      sourceAsset: {
        id: base,
        manifestId: null,
        type: 'sprite',
        category: 'prop',
        prompt: '',
      },
      layeredNodeId: null,
      layeredKind: null,
    });
    setOpenDialogOpen(false);
    setEditorOpen(true);
  };

  const confirmNewDocument = (doc) => {
    const width = doc.width;
    const height = doc.height;
    const assetLabel = doc.filename || `canvas_${width}x${height}`;
    setEditorError('');
    setEditorSuccess('');
    setSession({
      kind: 'blank',
      imageUrl: '',
      assetLabel,
      objectUrl: null,
      blankSize: { width, height },
      destinationProjectId: doc.destinationProjectId || null,
      preferredFormat: normalizeImageEditorFormat(doc.format),
      draftId: null,
      sourceAsset: {
        id: assetLabel,
        manifestId: null,
        type: 'sprite',
        category: 'prop',
        prompt: '',
      },
      layeredNodeId: null,
      layeredKind: null,
    });
    setNewDialogOpen(false);
    setEditorOpen(true);
  };

  const handleDocumentChange = (doc) => {
    setSession((prev) => {
      if (!prev) return prev;
      if (prev.objectUrl && prev.objectUrl !== doc.objectUrl) {
        try {
          URL.revokeObjectURL(prev.objectUrl);
        } catch {
          /* ignore */
        }
      }
      return {
        ...prev,
        kind: doc.kind,
        assetLabel: doc.assetLabel,
        sourceAsset: doc.sourceAsset,
        objectUrl: doc.objectUrl || null,
        // File→New / Open local inside the modal default to Drafts unless
        // the session already had a project destination.
        destinationProjectId:
          doc.kind === 'local' || doc.kind === 'blank'
            ? prev.destinationProjectId ?? null
            : prev.destinationProjectId,
        draftId: doc.kind === 'local' || doc.kind === 'blank' ? null : prev.draftId,
        layeredNodeId:
          doc.kind === 'local' || doc.kind === 'blank'
            ? null
            : prev.layeredNodeId || null,
        layeredKind:
          doc.kind === 'local' || doc.kind === 'blank'
            ? null
            : prev.layeredKind || null,
      };
    });
  };

  const openGalleryAsset = (asset, projectId) => {
    const imageUrl = galleryImageUrl(asset);
    if (!imageUrl) return;
    setEditorError('');
    setEditorSuccess('');
    const label = galleryLabel(asset);
    const manifestId =
      typeof asset.manifestId === 'string' ? asset.manifestId.trim() : '';
    const manifestAssetId =
      typeof asset.manifestAssetId === 'string'
        ? asset.manifestAssetId.trim()
        : '';
    setSession({
      kind: 'gallery',
      imageUrl,
      assetLabel: label,
      objectUrl: null,
      destinationProjectId: projectId || null,
      preferredFormat: 'png',
      draftId: null,
      sourceAsset: {
        id: manifestAssetId || label,
        manifestId: manifestId || null,
        type: typeof asset.type === 'string' ? asset.type : 'sprite',
        category: typeof asset.category === 'string' ? asset.category : 'prop',
        prompt:
          typeof asset.promptUsed === 'string'
            ? asset.promptUsed
            : typeof asset.prompt === 'string'
              ? asset.prompt
              : '',
        assetName: label,
      },
      layeredNodeId: null,
      layeredKind: null,
    });
    setOpenDialogOpen(false);
    setEditorOpen(true);
  };

  const openDraft = (draft) => {
    const imageUrl = galleryImageUrl(draft);
    if (!imageUrl && draft.editorDocumentKind !== IMAGE_EDITOR_PROJECT_KIND) {
      return;
    }
    setEditorError('');
    setEditorSuccess('');
    const label = galleryLabel(draft);
    const isLayered = draft.editorDocumentKind === IMAGE_EDITOR_PROJECT_KIND;
    setSession({
      kind: 'draft',
      imageUrl: imageUrl || '',
      assetLabel: label,
      objectUrl: null,
      destinationProjectId: null,
      preferredFormat: normalizeImageEditorFormat(draft.format),
      draftId: typeof draft.id === 'string' ? draft.id : null,
      layeredNodeId:
        isLayered && typeof draft.id === 'string' ? draft.id : null,
      layeredKind: isLayered ? IMAGE_EDITOR_PROJECT_KIND : null,
      sourceAsset: {
        id: label,
        manifestId: null,
        type: typeof draft.type === 'string' ? draft.type : 'sprite',
        category: typeof draft.category === 'string' ? draft.category : 'prop',
        prompt:
          typeof draft.promptUsed === 'string' ? draft.promptUsed : '',
        assetName: label,
      },
    });
    setOpenDialogOpen(false);
    setEditorOpen(true);
  };

  /**
   * Open a recent-document card — same load path as former Restore action
   * for layered autosaves / projects.
   */
  const openRecentDocument = (doc) => {
    if (!doc?.id) return;
    openDraft({
      id: doc.id,
      name: doc.name,
      assetName: doc.name,
      imageUrl: doc.imageUrl,
      previewUrl: doc.imageUrl,
      downloadUrl: doc.imageUrl,
      format: doc.format,
      editorDocumentKind: doc.isLayered
        ? IMAGE_EDITOR_PROJECT_KIND
        : doc.editorDocumentKind,
      layerCount: doc.layerCount,
      autosave: doc.isAutosave,
      type: doc.type,
      category: doc.category,
      promptUsed: doc.promptUsed,
    });
  };

  const uid = auth.currentUser?.uid || null;

  const writableRecent = useMemo(
    () =>
      recentDocuments.filter(
        (doc) =>
          doc.filesNode &&
          canWriteFilesNode({ node: doc.filesNode, uid, role }),
      ),
    [recentDocuments, uid, role],
  );

  const selectedRecent = useMemo(
    () => recentDocuments.filter((doc) => selectedIds.has(doc.id)),
    [recentDocuments, selectedIds],
  );

  const toggleRecentSelect = (docId, event, opts = {}) => {
    const orderedIds = recentDocuments.map((d) => d.id);
    // Checkboxes (and selection-mode card clicks) always toggle membership.
    // Plain card clicks without an existing selection stay exclusive — same
    // as Files row open/select — but the Files checkbox path was copied here
    // with exclusive semantics, which made multi-check impossible.
    const forceToggle = Boolean(opts.forceToggle);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (event?.shiftKey && lastClickedId) {
        const a = orderedIds.indexOf(lastClickedId);
        const b = orderedIds.indexOf(docId);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i += 1) next.add(orderedIds[i]);
          return next;
        }
      }
      if (forceToggle || event?.ctrlKey || event?.metaKey) {
        if (next.has(docId)) next.delete(docId);
        else next.add(docId);
        return next;
      }
      if (next.has(docId) && next.size === 1) {
        next.clear();
      } else {
        next.clear();
        next.add(docId);
      }
      return next;
    });
    setLastClickedId(docId);
  };

  const toggleSelectAllRecent = () => {
    if (
      writableRecent.length > 0 &&
      writableRecent.every((d) => selectedIds.has(d.id))
    ) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(writableRecent.map((d) => d.id)));
  };

  const refreshRecentDocuments = async () => {
    if (!resolvedCompanyId || !uid) return;
    setRecentLoading(true);
    try {
      const rows = await listRecentImageEditorDocuments({
        companyId: resolvedCompanyId,
        uid,
        maxAgeDays: 7,
        limit: 16,
      });
      setRecentDocuments(rows);
      setSelectedIds(new Set());
      setLastClickedId(null);
    } catch (err) {
      console.warn('ImageEditorHub recent refresh failed:', err);
      setRecentError(err?.message || 'Could not refresh recent documents.');
    } finally {
      setRecentLoading(false);
    }
  };

  const handleBulkDeleteRecent = async () => {
    const deletable = selectedRecent.filter(
      (doc) =>
        doc.filesNode && canWriteFilesNode({ node: doc.filesNode, uid, role }),
    );
    if (!deletable.length || recentBusy || !resolvedCompanyId) return;
    if (
      !window.confirm(
        `Delete ${deletable.length} selected document${
          deletable.length === 1 ? '' : 's'
        }? This cannot be undone.`,
      )
    ) {
      return;
    }
    setRecentBusy(true);
    setEditorError('');
    try {
      const result = await runBulkFilesOperation({
        items: deletable.map((d) => d.filesNode),
        runOne: (node) =>
          deleteFilesNode({
            companyId: resolvedCompanyId,
            node,
            uid,
            role,
          }),
      });
      const msg = formatBulkFilesResultMessage(result, 'deleted');
      if (result.failed.length) setEditorError(msg);
      else setEditorSuccess(msg);
      setSelectedIds(new Set());
      await refreshRecentDocuments();
    } catch (err) {
      console.error('ImageEditorHub bulk delete failed:', err);
      setEditorError(err?.message || 'Could not delete selected documents.');
    } finally {
      setRecentBusy(false);
    }
  };

  const handleSaveProject = async (payload = {}) => {
    if (!resolvedCompanyId || !session) return;
    const uid = auth.currentUser?.uid || null;
    if (!uid) throw new Error('You must be signed in to save projects.');
    const editorDoc = payload.document;
    if (!editorDoc) throw new Error('No editor document to save.');
    const result = await saveLayeredEditorProject({
      companyId: resolvedCompanyId,
      uid,
      doc: editorDoc,
      fileStem: payload.fileStem || session.assetLabel || 'project',
      nodeId: session.layeredNodeId || null,
      autosave: false,
      ownerScope: 'company',
    });
    setSession((prev) =>
      prev
        ? {
            ...prev,
            layeredNodeId: result.nodeId,
            layeredKind: IMAGE_EDITOR_PROJECT_KIND,
          }
        : prev,
    );
    return result;
  };

  const handleAutosaveProject = async (payload = {}) => {
    if (!resolvedCompanyId || !session) return null;
    const uid = auth.currentUser?.uid || null;
    if (!uid) return null;
    const editorDoc = payload.document;
    if (!editorDoc) return null;
    const result = await saveLayeredEditorProject({
      companyId: resolvedCompanyId,
      uid,
      doc: editorDoc,
      fileStem: payload.fileStem || session.assetLabel || 'autosave',
      nodeId: payload.nodeId || null,
      autosave: true,
      ownerScope: 'user',
    });
    return result;
  };

  const handleSave = async (blob, meta = {}) => {
    if (!resolvedCompanyId || !blob || !session) return;
    const projectId =
      typeof session.destinationProjectId === 'string' &&
      session.destinationProjectId.trim()
        ? session.destinationProjectId.trim()
        : '';
    setEditorBusy(true);
    setEditorError('');
    setEditorSuccess('');
    try {
      if (projectId) {
        const result = await saveEditedAssetVariant({
          companyId: resolvedCompanyId,
          projectId,
          blob,
          meta,
          sourceAsset: session.sourceAsset || null,
          uid: auth.currentUser?.uid || null,
        });
        setEditorSuccess(
          result.mode === 'manifest_variant'
            ? `Saved variant “${result.assetId}” to the project manifest + gallery.`
            : `Saved “${result.assetId}” as a new gallery asset (not linked to a manifest).`,
        );
      } else {
        const result = await saveImageEditorDraft({
          companyId: resolvedCompanyId,
          blob,
          meta,
          sourceAsset: session.sourceAsset || null,
          uid: auth.currentUser?.uid || null,
          draftId: session.draftId || null,
        });
        setEditorSuccess(`Saved “${result.assetId}” to Drafts.`);
      }
      closeEditor();
    } catch (err) {
      console.error('ImageEditorHub save:', err);
      setEditorError(err?.message || 'Could not save the edited image.');
    } finally {
      setEditorBusy(false);
    }
  };

  const saveDestinationLabel = session?.destinationProjectId
    ? 'Save to project'
    : 'Save to Drafts';

  if (!resolvedCompanyId) {
    return (
      <Alert severity="info" sx={{ m: 2, maxWidth: 720 }}>
        Join or select a company workspace to use the AI Image Editor.
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        px: 2,
        py: 2,
        gap: 2,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ImageOutlinedIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              AI Image Editor
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create or open an image to start editing. Saves without a project
              go to Files (Library → Image Editor).
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            onClick={() => {
              setEditorError('');
              setNewDialogOpen(true);
            }}
            sx={{ textTransform: 'none' }}
          >
            New
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setEditorError('');
              setOpenDialogOpen(true);
            }}
            sx={{ textTransform: 'none' }}
          >
            Open
          </Button>
        </Stack>
      </Stack>

      {editorError ? (
        <Alert severity="error" onClose={() => setEditorError('')}>
          {editorError}
        </Alert>
      ) : null}
      {editorSuccess ? (
        <Alert severity="success" onClose={() => setEditorSuccess('')}>
          {editorSuccess}
        </Alert>
      ) : null}
      {projectsError ? <Alert severity="warning">{projectsError}</Alert> : null}
      {recentError ? (
        <Alert severity="warning" onClose={() => setRecentError('')}>
          {recentError}
        </Alert>
      ) : null}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'action.hover',
          px: 2,
          py: 2,
          overflow: 'auto',
        }}
        data-testid="image-editor-recent-landing"
      >
        {recentLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : recentDocuments.length ? (
          <Stack spacing={1.5} sx={{ minHeight: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Recent documents
              </Typography>
              {writableRecent.length ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Checkbox
                    size="small"
                    checked={
                      writableRecent.length > 0 &&
                      writableRecent.every((d) => selectedIds.has(d.id))
                    }
                    indeterminate={
                      selectedIds.size > 0 &&
                      !writableRecent.every((d) => selectedIds.has(d.id))
                    }
                    onChange={toggleSelectAllRecent}
                    disabled={recentBusy}
                    inputProps={{ 'aria-label': 'Select all recent documents' }}
                    data-testid="image-editor-recent-select-all"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Select all
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
            {selectedRecent.length > 0 ? (
              <Paper
                variant="outlined"
                sx={{ px: 1.5, py: 1, bgcolor: 'action.selected' }}
                data-testid="image-editor-recent-bulk-bar"
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography variant="body2" fontWeight={600}>
                    {selectedRecent.length} selected
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleBulkDeleteRecent}
                    disabled={recentBusy}
                    sx={{ textTransform: 'none' }}
                    data-testid="image-editor-recent-bulk-delete"
                  >
                    Delete
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setSelectedIds(new Set())}
                    disabled={recentBusy}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear
                  </Button>
                  {recentBusy ? <CircularProgress size={16} /> : null}
                </Stack>
              </Paper>
            ) : null}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(3, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1.5,
              }}
            >
              {recentDocuments.map((doc) => {
                const thumb = doc.imageUrl || '';
                const metaParts = [];
                if (doc.isLayered) {
                  metaParts.push(
                    typeof doc.layerCount === 'number'
                      ? `${doc.layerCount} layers`
                      : 'Layered',
                  );
                }
                if (doc.isAutosave) metaParts.push('Autosave');
                metaParts.push(formatRelativeUpdated(doc.updatedAtMs));
                const canDelete = Boolean(
                  doc.filesNode &&
                    canWriteFilesNode({ node: doc.filesNode, uid, role }),
                );
                const selected = selectedIds.has(doc.id);
                return (
                  <Card
                    key={doc.id}
                    variant="outlined"
                    sx={{
                      bgcolor: selected ? 'action.selected' : 'background.paper',
                      borderColor: selected ? 'primary.main' : 'divider',
                      position: 'relative',
                    }}
                    data-testid="image-editor-recent-card"
                  >
                    {canDelete ? (
                      <Checkbox
                        size="small"
                        checked={selected}
                        disabled={recentBusy}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRecentSelect(doc.id, e, { forceToggle: true });
                        }}
                        onChange={() => {}}
                        sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2 }}
                        inputProps={{
                          'aria-label': `Select ${doc.name}`,
                          'data-testid': 'image-editor-recent-card-checkbox',
                        }}
                      />
                    ) : null}
                    <CardActionArea
                      onClick={(e) => {
                        if (e.shiftKey || e.ctrlKey || e.metaKey) {
                          if (canDelete) toggleRecentSelect(doc.id, e);
                          return;
                        }
                        if (selectedIds.size > 0 && canDelete) {
                          // Already multi-selecting — add/remove, don't replace.
                          toggleRecentSelect(doc.id, e, { forceToggle: true });
                          return;
                        }
                        openRecentDocument(doc);
                      }}
                      disabled={editorBusy || recentBusy}
                      sx={{ height: '100%', alignItems: 'stretch' }}
                    >
                      {thumb ? (
                        <CardMedia
                          component="img"
                          image={thumb}
                          alt={doc.name}
                          sx={{
                            height: 112,
                            objectFit: 'contain',
                            bgcolor: 'grey.900',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 112,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.900',
                          }}
                        >
                          <ImageOutlinedIcon sx={{ color: 'grey.500' }} />
                        </Box>
                      )}
                      <CardContent sx={{ py: 1, px: 1.25, '&:last-child': { pb: 1.25 } }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={doc.name}>
                          {doc.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {metaParts.join(' · ')}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>
          </Stack>
        ) : (
          <Stack
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ flex: 1, maxWidth: 420, textAlign: 'center', mx: 'auto', py: 6 }}
          >
            <ImageOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Ready to edit
            </Typography>
            <Typography variant="body2" color="text.secondary">
              New opens a blank canvas. Open browses project galleries, Drafts, or
              a local file. Zoom/pan stay viewport-only.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => setNewDialogOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                New
              </Button>
              <Button
                variant="outlined"
                onClick={() => setOpenDialogOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Open
              </Button>
            </Stack>
            {projectsLoading ? <CircularProgress size={20} /> : null}
          </Stack>
        )}
      </Box>

      <ImageEditorNewDocumentDialog
        open={newDialogOpen}
        projects={projects}
        projectsLoading={projectsLoading}
        busy={editorBusy}
        onCancel={() => setNewDialogOpen(false)}
        onCreate={confirmNewDocument}
      />

      <ImageEditorOpenDialog
        open={openDialogOpen}
        companyId={resolvedCompanyId}
        projects={projects}
        projectsLoading={projectsLoading}
        busy={editorBusy}
        onCancel={() => setOpenDialogOpen(false)}
        onOpenGalleryAsset={openGalleryAsset}
        onOpenDraft={openDraft}
        onOpenLocalFile={openLocalFile}
      />

      <ImageEditorModal
        open={editorOpen}
        imageUrl={session?.imageUrl || ''}
        layeredNodeId={session?.layeredNodeId || null}
        blankSize={session?.blankSize}
        assetLabel={session?.assetLabel || ''}
        busy={editorBusy}
        companyId={resolvedCompanyId}
        projectId={session?.destinationProjectId || null}
        preferredFormat={session?.preferredFormat || 'png'}
        onClose={closeEditor}
        onSave={handleSave}
        onSaveProject={handleSaveProject}
        onAutosaveProject={handleAutosaveProject}
        onDocumentChange={handleDocumentChange}
        saveToProjectDisabled={false}
        saveToProjectLabel={saveDestinationLabel}
      />
    </Box>
  );
}
